import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  CreditCard, 
  Calendar,
  DollarSign,
  ArrowUpDown,
  Filter,
  RefreshCw,
  Download,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  MoreHorizontal,
  History,
  PlusCircle,
  Tags,
  Settings,
  ExternalLink,
  XCircle,
  BarChart3,
  Edit,
  Copy,
  Eye,
  Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Define types
interface Plan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  isPopular: boolean;
  stripeId: string;
}

interface Subscription {
  id: number;
  tenantId: number;
  tenantName: string;
  planType: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  startDate: string;
  endDate: string | null;
  amount: number;
  currency: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  created: number;
  updated: number;
  prices: StripePrice[];
}

interface StripePrice {
  id: string;
  productId: string;
  nickname: string;
  unitAmount: number;
  currency: string;
  interval: 'month' | 'year' | 'week' | 'day';
  active: boolean;
  created: number;
}

interface Invoice {
  id: string;
  tenantId: number;
  tenantName: string;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  date: string;
  dueDate: string;
  amount: number;
  amountPaid: number;
  currency: string;
  description: string;
}

// Interface for Stripe settings
interface StripeSettings {
  mode: 'test' | 'live';
  publishableKey: string;
  secretKey?: string;
  webhookSecret?: string;
  webhookUrl: string;
  events: string[];
}

export default function BillingPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedPlan, setSelectedPlan] = useState<StripeProduct | null>(null);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('test');
  const [publishableKey, setPublishableKey] = useState("");
  const [secretKey, setSecretKey] = useState(() => {
    // Try to load from localStorage on page load
    return localStorage.getItem('stripeSecretKey') || "";
  });
  const [webhookSecret, setWebhookSecret] = useState(() => {
    // Try to load from localStorage on page load
    return localStorage.getItem('stripeWebhookSecret') || "";
  });
  const [webhookUrl, setWebhookUrl] = useState(() => {
    // Try to load from localStorage on page load, or use default
    return localStorage.getItem('stripeWebhookUrl') || "https://saascompass.venpep.net/api/webhooks/stripe";
  });
  const [verifyingConnection, setVerifyingConnection] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  
  // Fetch subscription data
  const { data: subscriptions = [], isLoading: subscriptionsLoading } = useQuery<Subscription[]>({
    queryKey: ["/api/admin/system/subscriptions"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/subscriptions", {
          method: "GET",
        }) as Subscription[];
        return response;
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        return [];
      }
    },
  });

  // Fetch Stripe products
  const { data: stripeProducts = [], isLoading: productsLoading } = useQuery<StripeProduct[]>({
    queryKey: ["/api/admin/system/stripe/products"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/stripe/products", {
          method: "GET",
        }) as StripeProduct[];
        return response;
      } catch (error) {
        console.error("Error fetching Stripe products:", error);
        return [];
      }
    },
  });

  // Fetch invoices
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<Invoice[]>({
    queryKey: ["/api/admin/system/invoices"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/invoices", {
          method: "GET",
        }) as Invoice[];
        return response;
      } catch (error) {
        console.error("Error fetching invoices:", error);
        return [];
      }
    },
  });
  
  // Fetch Stripe settings
  const { data: stripeSettings, isLoading: settingsLoading } = useQuery<StripeSettings>({
    queryKey: ["/api/admin/system/stripe/settings"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/stripe/settings", {
          method: "GET",
        }) as StripeSettings;
        return response;
      } catch (error) {
        console.error("Error fetching Stripe settings:", error);
        // Return default settings
        return {
          mode: 'test',
          publishableKey: '',
          webhookUrl: '',
          events: []
        };
      }
    },
  });

  // Filter subscriptions based on search query and filters
  const filteredSubscriptions = subscriptions.filter(subscription => {
    // Apply search filter
    const matchesSearch = 
      subscription.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subscription.planName.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply status filter
    const matchesStatus = statusFilter === "all" || subscription.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Fetch MRR from dashboard stats endpoint (has correct $1,507 calculation)
  const { data: dashboardStats } = useQuery({
    queryKey: ["/api/admin/system/dashboard-stats"],
    queryFn: () => apiRequest("/api/admin/system/dashboard-stats")
  });

  // Calculate summary metrics using subscription data
  const activeSubscriptions = subscriptions.filter(s => s.status === 'active' || s.status === 'trialing');
  const totalMRR = dashboardStats?.mrr || 0;
  
  // Format currency for Stripe prices (convert cents to dollars)
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount / 100); // Convert Stripe cents to dollars
  };

  // Format currency for values already in dollars (like MRR from backend)
  const formatDollars = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount); // Already in dollars
  };

  // Update local state when Stripe settings are loaded
  useEffect(() => {
    if (stripeSettings) {
      setStripeMode(stripeSettings.mode || 'test');
      setPublishableKey(stripeSettings.publishableKey || '');
      
      // Make sure to preserve user entered values for secret fields
      if (!secretKey && stripeSettings.secretKey) {
        setSecretKey(stripeSettings.secretKey);
      }
      
      if (!webhookSecret && stripeSettings.webhookSecret) {
        setWebhookSecret(stripeSettings.webhookSecret);
      }
      
      // Update webhook URL if available and not already set by user
      if (stripeSettings.webhookUrl && webhookUrl === "https://saaspulse.venpep.net/api/webhooks/stripe") {
        setWebhookUrl(stripeSettings.webhookUrl);
      }
    }
  }, [stripeSettings]);
  
  // Verify Stripe connection
  const handleVerifyConnection = async () => {
    try {
      setVerifyingConnection(true);
      
      // apiRequest already returns JSON data
      const result = await apiRequest("/api/admin/system/stripe/verify", {
        method: "POST"
      });
      
      if (result.success) {
        toast({
          title: "Connection verified",
          description: "Successfully connected to Stripe API",
        });
      } else {
        toast({
          title: "Verification failed",
          description: result.message || "Failed to connect to Stripe API",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error verifying Stripe connection:", error);
      toast({
        title: "Verification failed",
        description: "Failed to connect to Stripe API. See console for details.",
        variant: "destructive",
      });
    } finally {
      setVerifyingConnection(false);
    }
  };
  
  // Update Stripe settings
  const handleUpdateSettings = async () => {
    try {
      setUpdatingSettings(true);
      
      // Create settings payload, including webhook URL
      const settings = {
        mode: stripeMode,
        publishableKey,
        webhookUrl,
        // Always include the current secretKey and webhookSecret values
        secretKey,
        webhookSecret
      };
      
      console.log("Sending settings update:", settings);
      
      // Use apiRequest instead of fetch directly - it handles JSON parsing
      const result = await apiRequest("/api/admin/system/stripe/settings", {
        method: "POST",
        body: settings
      });
      console.log("Settings update response:", result);
      
      if (result) {
        toast({
          title: "Settings updated",
          description: "Stripe settings have been successfully updated",
        });
        
        // Store the current values in local storage to persist them
        localStorage.setItem('stripeSecretKey', secretKey);
        localStorage.setItem('stripeWebhookSecret', webhookSecret);
        localStorage.setItem('stripeWebhookUrl', webhookUrl);
        
        // Invalidate the settings query to refresh data
        queryClient.invalidateQueries({ queryKey: ["/api/admin/system/stripe/settings"] });
      } else {
        toast({
          title: "Update failed",
          description: result.message || "Failed to update Stripe settings",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating Stripe settings:", error);
      toast({
        title: "Update failed",
        description: "Failed to update Stripe settings. See console for details.",
        variant: "destructive",
      });
    } finally {
      setUpdatingSettings(false);
    }
  };

  // Generate status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
      case 'trialing':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Trial</Badge>;
      case 'past_due':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="outline" className="text-slate-500">Canceled</Badge>;
      case 'unpaid':
        return <Badge variant="destructive">Unpaid</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case 'open':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Open</Badge>;
      case 'void':
        return <Badge variant="outline" className="text-slate-500">Void</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'uncollectible':
        return <Badge variant="destructive">Uncollectible</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Handle Stripe sync
  const handleStripeSync = async () => {
    try {
      // Show initial toast
      toast({
        title: "Stripe sync initiated",
        description: "Syncing products, prices, and subscriptions from Stripe",
      });
      
      // Make API call to sync with Stripe
      await apiRequest("/api/admin/system/stripe/sync", {
        method: "POST",
        body: {
          syncProducts: true,
          syncSubscriptions: true,
          syncInvoices: true
        }
      });
      
      // Invalidate queries to reload data
      queryClient.invalidateQueries({ queryKey: ["/api/admin/system/stripe/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/system/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/system/invoices"] });
      
      // Show success toast
      toast({
        title: "Sync completed",
        description: "Successfully synchronized data with Stripe",
      });
    } catch (error) {
      console.error("Error syncing with Stripe:", error);
      toast({
        title: "Sync failed",
        description: "Failed to synchronize data with Stripe. See console for details.",
        variant: "destructive",
      });
    } finally {
      setShowSyncDialog(false);
    }
  };

  // Handle opening product details
  const handleOpenProductDetails = (product: StripeProduct) => {
    setSelectedPlan(product);
    setShowPlanDetails(true);
  };

  return (
    <SuperAdminLayout>
      <div className="container mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Billing Management</h1>
            <p className="text-muted-foreground">
              Manage Stripe integration, products, pricing, and subscriptions
            </p>
          </div>
          
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Stripe Dashboard
            </Button>
            
            <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
              <DialogTrigger asChild>
                <Button className="space-x-2">
                  <RefreshCw className="h-4 w-4" />
                  <span>Sync with Stripe</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Sync with Stripe</DialogTitle>
                  <DialogDescription>
                    This will synchronize all products, prices, and subscriptions from your Stripe account.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-6 space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="sync-products" defaultChecked />
                    <label htmlFor="sync-products">Sync Products & Prices</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="sync-subscriptions" defaultChecked />
                    <label htmlFor="sync-subscriptions">Sync Subscriptions</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="sync-invoices" defaultChecked />
                    <label htmlFor="sync-invoices">Sync Invoices</label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSyncDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleStripeSync}>
                    Start Sync
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Monthly Recurring Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{formatDollars(totalMRR)}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{activeSubscriptions.length}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Invoices (30 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{invoices.length}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="subscriptions">
          <TabsList className="mb-4">
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="products">Products & Pricing</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="settings">Stripe Settings</TabsTrigger>
          </TabsList>
          
          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search subscriptions..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="flex space-x-2">
                <div className="flex items-center space-x-1">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Filter:</span>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trialing">Trial</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Tenant Subscriptions</CardTitle>
                    <CardDescription>
                      {subscriptionsLoading
                        ? "Loading subscription data..."
                        : `${filteredSubscriptions.length} subscription records`}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {subscriptionsLoading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-10 h-10 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading subscription data...</p>
                  </div>
                ) : filteredSubscriptions.length === 0 ? (
                  <div className="text-center py-10">
                    <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-1">No subscriptions found</h3>
                    <p className="text-muted-foreground">
                      {searchQuery || statusFilter !== "all"
                        ? `No results found with the current filters`
                        : "There are no subscriptions in the system yet"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">
                            <div className="flex items-center">
                              Tenant
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Start Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Stripe ID</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSubscriptions.map((subscription) => (
                          <TableRow key={subscription.id}>
                            <TableCell className="font-medium">{subscription.tenantName}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                subscription.planType === 'enterprise' ? 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100' :
                                subscription.planType === 'professional' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' :
                                subscription.planType === 'starter' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' :
                                'bg-slate-100 text-slate-800 hover:bg-slate-100'
                              }>
                                {subscription.planName}
                              </Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(subscription.status)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                {new Date(subscription.startDate).toLocaleDateString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              {subscription.amount > 0 ? formatCurrency(subscription.amount) : 'Free'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {subscription.stripeSubscriptionId || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem>
                                    <History className="mr-2 h-4 w-4" />
                                    View Invoices
                                  </DropdownMenuItem>
                                  <DropdownMenuItem>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Change Plan
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Add Credit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-red-600">
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Cancel Subscription
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Products & Pricing Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-semibold">Products & Pricing</h2>
                <p className="text-sm text-muted-foreground">
                  Manage your Stripe products and pricing plans
                </p>
              </div>
              
              <Button>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add New Product
              </Button>
            </div>

            {productsLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin w-10 h-10 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading product data...</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {stripeProducts.map((product) => (
                  <Card key={product.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{product.name}</CardTitle>
                          <CardDescription>{product.description}</CardDescription>
                        </div>
                        <Badge variant={product.active ? "default" : "outline"}>
                          {product.active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <h3 className="text-sm font-medium mb-2">Pricing</h3>
                      <div className="space-y-2">
                        {product.prices.map((price) => (
                          <div key={price.id} className="flex justify-between items-center p-2 bg-muted rounded-md">
                            <div>
                              <div className="font-medium">{price.nickname || `${product.name} ${price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly`}</div>
                              <div className="text-xs text-muted-foreground">{price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold">{formatCurrency(price.unitAmount)}</div>
                              <div className="text-xs text-muted-foreground">
                                {price.active ? "Active" : "Inactive"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                    <CardFooter className="justify-between">
                      <div className="text-xs text-muted-foreground">
                        ID: {product.id}
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleOpenProductDetails(product)}>
                          View Details
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}

            {/* Product Details Dialog */}
            {selectedPlan && (
              <Dialog open={showPlanDetails} onOpenChange={setShowPlanDetails}>
                <DialogContent className="sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle>{selectedPlan.name} Plan Details</DialogTitle>
                    <DialogDescription>
                      Product ID: {selectedPlan.id}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Product Information</h3>
                        <div className="space-y-2">
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-sm text-muted-foreground">Name:</div>
                            <div className="col-span-2">{selectedPlan.name}</div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-sm text-muted-foreground">Description:</div>
                            <div className="col-span-2">{selectedPlan.description}</div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-sm text-muted-foreground">Status:</div>
                            <div className="col-span-2">
                              <Badge variant={selectedPlan.active ? "default" : "outline"}>
                                {selectedPlan.active ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-sm text-muted-foreground">Created:</div>
                            <div className="col-span-2">{new Date(selectedPlan.created * 1000).toLocaleString()}</div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold mb-2">Price Points</h3>
                        <Accordion type="single" collapsible className="w-full">
                          {selectedPlan.prices.map((price) => (
                            <AccordionItem key={price.id} value={price.id}>
                              <AccordionTrigger>
                                <div className="flex justify-between items-center w-full mr-4">
                                  <div>{price.nickname || `${price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly`}</div>
                                  <div>{formatCurrency(price.unitAmount)}</div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-2 pl-2">
                                  <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-1 text-sm text-muted-foreground">Price ID:</div>
                                    <div className="col-span-2 text-sm">{price.id}</div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-1 text-sm text-muted-foreground">Interval:</div>
                                    <div className="col-span-2">{price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly</div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-1 text-sm text-muted-foreground">Status:</div>
                                    <div className="col-span-2">
                                      <Badge variant={price.active ? "default" : "outline"}>
                                        {price.active ? "Active" : "Inactive"}
                                      </Badge>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-1 text-sm text-muted-foreground">Created:</div>
                                    <div className="col-span-2">{new Date(price.created * 1000).toLocaleString()}</div>
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowPlanDetails(false)}>
                      Close
                    </Button>
                    <Button>
                      Edit Product
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>
          
          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-semibold">Invoices</h2>
                <p className="text-sm text-muted-foreground">
                  View and manage all invoices
                </p>
              </div>
              
              <div className="flex space-x-2">
                <Button variant="outline">
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                {invoicesLoading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-10 h-10 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading invoice data...</p>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-10">
                    <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-1">No invoices found</h3>
                    <p className="text-muted-foreground">
                      There are no invoices in the system yet
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Tenant</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">{invoice.number}</TableCell>
                            <TableCell>{invoice.tenantName}</TableCell>
                            <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                            <TableCell>{new Date(invoice.dueDate).toLocaleDateString()}</TableCell>
                            <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                            <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                            <TableCell>
                              <div className="flex space-x-2">
                                <Button variant="ghost" size="sm">
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm">
                                  <ArrowRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Stripe API Configuration</CardTitle>
                <CardDescription>
                  Configure your Stripe API keys and webhook settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {settingsLoading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-10 h-10 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading Stripe settings...</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="stripe-mode">Environment Mode</Label>
                      <Select 
                        value={stripeMode} 
                        onValueChange={(value) => setStripeMode(value as 'test' | 'live')}
                      >
                        <SelectTrigger id="stripe-mode">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="test">Test Mode</SelectItem>
                          <SelectItem value="live">Live Mode</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="stripe-pk">Publishable Key</Label>
                      <div className="flex">
                        <Input
                          id="stripe-pk"
                          type="text"
                          value={publishableKey}
                          onChange={(e) => setPublishableKey(e.target.value)}
                          className="flex-1"
                          placeholder="Enter your publishable key"
                        />
                        <Button 
                          variant="ghost" 
                          className="ml-2 p-2" 
                          onClick={() => {
                            navigator.clipboard.writeText(publishableKey);
                            toast({
                              description: "Publishable key copied to clipboard",
                            });
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="stripe-sk">Secret Key</Label>
                      <div className="flex">
                        <Input
                          id="stripe-sk"
                          type="password"
                          value={secretKey}
                          onChange={(e) => setSecretKey(e.target.value)}
                          className="flex-1"
                          placeholder="Enter your Stripe secret key"
                        />
                        <Button 
                          variant="ghost" 
                          className="ml-2 p-2" 
                          onClick={() => {
                            navigator.clipboard.writeText(secretKey);
                            toast({
                              description: "Secret key copied to clipboard",
                            });
                          }}
                          disabled={!secretKey}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        This key is stored securely and never displayed once saved
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="webhook-secret">Webhook Secret</Label>
                      <div className="flex">
                        <Input
                          id="webhook-secret"
                          type="password"
                          value={webhookSecret}
                          onChange={(e) => setWebhookSecret(e.target.value)}
                          className="flex-1"
                          placeholder="Enter your webhook secret key"
                        />
                        <Button 
                          variant="ghost" 
                          className="ml-2 p-2" 
                          onClick={() => {
                            navigator.clipboard.writeText(webhookSecret);
                            toast({
                              description: "Webhook secret copied to clipboard",
                            });
                          }}
                          disabled={!webhookSecret}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Found in the Stripe Dashboard under Webhooks settings
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={handleVerifyConnection}
                  disabled={verifyingConnection || settingsLoading}
                >
                  {verifyingConnection ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Verify Connection
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleUpdateSettings}
                  disabled={updatingSettings || settingsLoading}
                >
                  {updatingSettings ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Settings className="mr-2 h-4 w-4" />
                      Update Settings
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Webhook Configuration</CardTitle>
                <CardDescription>
                  Manage Stripe webhook events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Webhook URL</Label>
                    <div className="flex mt-1">
                      <Input
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        className="flex-1"
                        placeholder="Enter your webhook URL"
                      />
                      <Button 
                        variant="outline" 
                        className="ml-2"
                        onClick={() => {
                          navigator.clipboard.writeText(webhookUrl);
                          toast({
                            description: "Webhook URL copied to clipboard",
                          });
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add this URL to your Stripe Dashboard webhook settings
                    </p>
                  </div>

                  <div>
                    <Label className="mb-2 block">Monitored Events</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-1" defaultChecked />
                        <label htmlFor="event-1" className="text-sm">
                          customer.subscription.created
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-2" defaultChecked />
                        <label htmlFor="event-2" className="text-sm">
                          customer.subscription.updated
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-3" defaultChecked />
                        <label htmlFor="event-3" className="text-sm">
                          customer.subscription.deleted
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-4" defaultChecked />
                        <label htmlFor="event-4" className="text-sm">
                          invoice.paid
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-5" defaultChecked />
                        <label htmlFor="event-5" className="text-sm">
                          invoice.payment_failed
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="event-6" defaultChecked />
                        <label htmlFor="event-6" className="text-sm">
                          customer.created
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={() => {
                  // Save the webhook settings
                  toast({
                    title: "Settings saved",
                    description: "Webhook URL and settings updated successfully",
                  });
                  // Simulate successful update for now - in a real implementation, this would send the data to your backend
                  // You can implement a real API call to save these settings when you're ready
                  console.log("Webhook URL saved:", webhookUrl);
                }}>
                  Save Webhook Settings
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </SuperAdminLayout>
  );
}

// Helper Components

interface IconProps {
  [key: string]: any;
}

const FileText = (props: IconProps) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" x2="8" y1="13" y2="13" />
      <line x1="16" x2="8" y1="17" y2="17" />
      <line x1="10" x2="8" y1="9" y2="9" />
    </svg>
  );
};



const Mail = (props: IconProps) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
};